import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  LogOut,
  Upload,
  FileVideo,
  FileText,
  Download,
  Loader2,
  CheckCircle,
  AlertCircle,
  Trash2,
  Clock,
  ListChecks,
  Target,
  FileOutput,
  Play,
  RefreshCw,
  Video,
  VideoOff,
  Mic,
  MicOff,
  Monitor,
  StopCircle,
  Circle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const screenStreamRef = useRef(null);
  const audioStreamRef = useRef(null);
  
  const [user, setUser] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [recordedVideoUrl, setRecordedVideoUrl] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/login');
      return;
    }
    
    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'customer') {
      navigate('/login');
      return;
    }
    
    setUser(parsedUser);
    fetchAnalyses();
  }, [navigate]);

  // Timer for recording
  useEffect(() => {
    let interval;
    if (isRecording && !isPaused) {
      interval = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRecording, isPaused]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAllStreams();
      if (recordedVideoUrl) {
        URL.revokeObjectURL(recordedVideoUrl);
      }
    };
  }, []);

  const stopAllStreams = () => {
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
      screenStreamRef.current = null;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }
  };

  const startRecording = async () => {
    try {
      // Request screen capture with system audio
      // IMPORTANT: User must check "Share system audio" in the Chrome dialog
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: "monitor",
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 }
        },
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        }
      });
      screenStreamRef.current = screenStream;

      // Check if system audio was shared
      const hasSystemAudio = screenStream.getAudioTracks().length > 0;
      if (!hasSystemAudio) {
        toast.warning('Audio del sistema no compartido. Marque "Compartir audio del sistema" en el diálogo.');
      }

      // Request microphone
      let micStream = null;
      try {
        micStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 48000
          }
        });
        audioStreamRef.current = micStream;
        toast.success('Micrófono conectado');
      } catch (micError) {
        console.warn('Microphone not available:', micError);
        toast.warning('Micrófono no disponible. Grabando solo audio del sistema.');
      }

      // Create combined stream with video and mixed audio
      let combinedStream;
      
      if (hasSystemAudio || micStream) {
        // Use AudioContext to mix audio sources
        const audioContext = new AudioContext({ sampleRate: 48000 });
        const destination = audioContext.createMediaStreamDestination();

        // Add system audio if available
        if (hasSystemAudio) {
          const systemSource = audioContext.createMediaStreamSource(
            new MediaStream(screenStream.getAudioTracks())
          );
          systemSource.connect(destination);
        }

        // Add microphone audio if available
        if (micStream) {
          const micSource = audioContext.createMediaStreamSource(micStream);
          // Increase mic volume slightly to ensure it's audible
          const micGain = audioContext.createGain();
          micGain.gain.value = 1.2;
          micSource.connect(micGain);
          micGain.connect(destination);
        }

        // Combine video track with mixed audio
        combinedStream = new MediaStream([
          ...screenStream.getVideoTracks(),
          ...destination.stream.getAudioTracks()
        ]);
      } else {
        // No audio available, just video
        combinedStream = new MediaStream(screenStream.getVideoTracks());
        toast.warning('Sin audio. Grabando solo video.');
      }

      // Setup MediaRecorder with best codec for audio
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
        ? 'video/webm;codecs=vp9,opus'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
          ? 'video/webm;codecs=vp8,opus'
          : 'video/webm';
      
      const mediaRecorder = new MediaRecorder(combinedStream, {
        mimeType,
        videoBitsPerSecond: 2500000,
        audioBitsPerSecond: 128000
      });
      mediaRecorderRef.current = mediaRecorder;
      recordedChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
        setRecordedVideoUrl(url);
        setRecordedBlob(blob);
        setShowRecordingModal(true);
        stopAllStreams();
      };

      // Handle when user stops sharing
      screenStream.getVideoTracks()[0].onended = () => {
        if (isRecording) {
          stopRecording();
        }
      };

      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      
      const audioStatus = [];
      if (hasSystemAudio) audioStatus.push('sistema');
      if (micStream) audioStatus.push('micrófono');
      toast.success(`Grabación iniciada (audio: ${audioStatus.length > 0 ? audioStatus.join(' + ') : 'ninguno'})`);

    } catch (error) {
      console.error('Error starting recording:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Permiso denegado para grabar pantalla');
      } else {
        toast.error('Error al iniciar grabación: ' + error.message);
      }
      stopAllStreams();
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    setIsPaused(false);
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      toast.info('Grabación pausada');
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      toast.info('Grabación reanudada');
    }
  };

  const cancelRecording = () => {
    setShowRecordingModal(false);
    setRecordedVideoUrl(null);
    setRecordedBlob(null);
    recordedChunksRef.current = [];
  };

  const downloadRecording = () => {
    if (recordedVideoUrl) {
      const a = document.createElement('a');
      a.href = recordedVideoUrl;
      a.download = `grabacion_${new Date().toISOString().slice(0,10)}.webm`;
      a.click();
      toast.success('Video descargado');
    }
  };

  const uploadRecording = async () => {
    if (!recordedBlob) return;

    setIsUploading(true);
    setUploadProgress(0);
    setShowRecordingModal(false);

    const formData = new FormData();
    const filename = `grabacion_${new Date().toISOString().slice(0,19).replace(/[:.]/g, '-')}.webm`;
    formData.append('file', recordedBlob, filename);
    formData.append('language', 'es');

    try {
      const response = await axios.post(`${API}/customer/upload-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      toast.success('¡Grabación procesada exitosamente!');
      fetchAnalyses();
      setSelectedAnalysis(response.data);
      setShowDetailModal(true);
      
      // Cleanup
      setRecordedVideoUrl(null);
      setRecordedBlob(null);
      recordedChunksRef.current = [];

    } catch (error) {
      console.error('Error uploading recording:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar la grabación');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const fetchAnalyses = async () => {
    try {
      const response = await axios.get(`${API}/customer/analyses`);
      setAnalyses(response.data);
    } catch (error) {
      console.error('Error fetching analyses:', error);
      toast.error('Error al cargar los análisis');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato no soportado. Use: MP4, MPEG, MOV, AVI o WebM');
      return;
    }

    // Validate file size (100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error('El archivo no debe superar 100MB');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('language', 'es');

    try {
      const response = await axios.post(`${API}/customer/upload-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      toast.success('¡Video procesado exitosamente!');
      fetchAnalyses();
      
      // Show the result
      setSelectedAnalysis(response.data);
      setShowDetailModal(true);
      
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar el video');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownload = async (analysisId) => {
    try {
      const response = await axios.get(`${API}/customer/analyses/${analysisId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Analisis_Video_${analysisId.slice(0, 8)}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Documento descargado');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Error al descargar el documento');
    }
  };

  const handleDelete = async (analysisId) => {
    if (!window.confirm('¿Está seguro de eliminar este análisis?')) return;
    
    try {
      await axios.delete(`${API}/customer/analyses/${analysisId}`);
      toast.success('Análisis eliminado');
      fetchAnalyses();
      if (selectedAnalysis?.id === analysisId) {
        setShowDetailModal(false);
        setSelectedAnalysis(null);
      }
    } catch (error) {
      console.error('Error deleting analysis:', error);
      toast.error('Error al eliminar el análisis');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const viewAnalysis = async (analysisId) => {
    try {
      const response = await axios.get(`${API}/customer/analyses/${analysisId}`);
      setSelectedAnalysis(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching analysis:', error);
      toast.error('Error al cargar el análisis');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3" />
            Completado
          </span>
        );
      case 'processing':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            <Clock className="w-3 h-3 animate-spin" />
            Procesando
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <AlertCircle className="w-3 h-3" />
            Error
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('es-EC', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-[#2F3D8E]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_3ee71ab0-856c-41a8-8e43-f382a70126f5/artifacts/3okyucn0_LOGO%20ANALYTICA.png" 
                alt="Analytica" 
                className="h-10"
              />
              <div>
                <h1 className="text-xl font-bold text-[#2F3D8E]">Portal Customer</h1>
                <p className="text-sm text-gray-500">Análisis de Video con IA</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">Bienvenido, <strong>{user?.username}</strong></span>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Cerrar sesión
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Screen Recording Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-xl font-bold text-[#2F3D8E] mb-2">Grabar Pantalla y Audio</h2>
          <p className="text-gray-600 mb-6">
            Grabe reuniones, presentaciones o tutoriales directamente desde su navegador. El audio del sistema y micrófono se capturan automáticamente.
          </p>

          <div className="flex flex-col items-center gap-6">
            {!isRecording ? (
              <Button
                onClick={startRecording}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg rounded-xl"
                data-testid="start-recording-btn"
              >
                <Circle className="w-6 h-6 mr-3 fill-current" />
                Iniciar Grabación
              </Button>
            ) : (
              <div className="flex flex-col items-center gap-4">
                {/* Recording Indicator */}
                <div className="flex items-center gap-3 bg-red-100 px-6 py-3 rounded-full">
                  <div className="w-4 h-4 bg-red-600 rounded-full animate-pulse" />
                  <span className="text-red-700 font-bold text-xl">{formatTime(recordingTime)}</span>
                  <span className="text-red-600 font-medium">
                    {isPaused ? 'PAUSADO' : 'GRABANDO'}
                  </span>
                </div>

                {/* Recording Controls */}
                <div className="flex items-center gap-3">
                  {!isPaused ? (
                    <Button
                      onClick={pauseRecording}
                      variant="outline"
                      className="px-6 py-3"
                      data-testid="pause-recording-btn"
                    >
                      <VideoOff className="w-5 h-5 mr-2" />
                      Pausar
                    </Button>
                  ) : (
                    <Button
                      onClick={resumeRecording}
                      variant="outline"
                      className="px-6 py-3"
                      data-testid="resume-recording-btn"
                    >
                      <Play className="w-5 h-5 mr-2" />
                      Reanudar
                    </Button>
                  )}
                  <Button
                    onClick={stopRecording}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-3"
                    data-testid="stop-recording-btn"
                  >
                    <StopCircle className="w-5 h-5 mr-2" />
                    Detener
                  </Button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full mt-4">
              <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
                <Monitor className="w-8 h-8 text-red-600" />
                <div>
                  <p className="font-medium text-[#2F3D8E]">Pantalla</p>
                  <p className="text-xs text-gray-600">Captura de pantalla completa</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-orange-50 rounded-lg">
                <Mic className="w-8 h-8 text-orange-600" />
                <div>
                  <p className="font-medium text-[#2F3D8E]">Micrófono</p>
                  <p className="text-xs text-gray-600">Audio de voz</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                <Video className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="font-medium text-[#2F3D8E]">Sistema</p>
                  <p className="text-xs text-gray-600">Audio del sistema</p>
                </div>
              </div>
            </div>

            {/* Audio Instructions */}
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm">
              <p className="font-medium text-amber-800 mb-2">⚠️ Para capturar audio correctamente:</p>
              <ul className="text-amber-700 space-y-1 list-disc list-inside">
                <li>Al iniciar, seleccione una <strong>pestaña de Chrome</strong> o <strong>ventana completa</strong></li>
                <li>Marque la casilla <strong>"Compartir audio de la pestaña"</strong> o <strong>"Compartir audio del sistema"</strong></li>
                <li>Permita el acceso al <strong>micrófono</strong> cuando se le solicite</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Upload Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-xl font-bold text-[#2F3D8E] mb-2">Subir Video para Análisis</h2>
          <p className="text-gray-600 mb-6">
            Suba un video y nuestra IA lo transcribirá y analizará automáticamente, extrayendo puntos clave, tareas y un resumen ejecutivo.
          </p>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="video/mp4,video/mpeg,video/quicktime,video/x-msvideo,video/webm"
            className="hidden"
          />

          {isUploading ? (
            <div className="border-2 border-dashed border-[#4C8C3B] rounded-xl p-8 text-center bg-green-50">
              <Loader2 className="w-12 h-12 mx-auto text-[#4C8C3B] animate-spin mb-4" />
              <p className="text-[#4C8C3B] font-medium mb-2">Procesando video...</p>
              <div className="w-full max-w-md mx-auto bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-[#4C8C3B] h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-sm text-gray-500 mt-2">
                {uploadProgress < 100 ? `Subiendo: ${uploadProgress}%` : 'Analizando con IA...'}
              </p>
            </div>
          ) : (
            <div 
              className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-[#4C8C3B] hover:bg-gray-50 transition-all"
              onClick={() => fileInputRef.current?.click()}
            >
              <FileVideo className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-700 mb-2">
                Haga clic para seleccionar un video
              </p>
              <p className="text-sm text-gray-500">
                MP4, MPEG, MOV, AVI, WebM (máximo 100MB)
              </p>
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
              <FileText className="w-8 h-8 text-[#5DA5D8]" />
              <div>
                <p className="font-medium text-[#2F3D8E]">Transcripción</p>
                <p className="text-xs text-gray-600">Audio a texto automático</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
              <Target className="w-8 h-8 text-[#4C8C3B]" />
              <div>
                <p className="font-medium text-[#2F3D8E]">Puntos Clave</p>
                <p className="text-xs text-gray-600">Extracción automática</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg">
              <ListChecks className="w-8 h-8 text-purple-600" />
              <div>
                <p className="font-medium text-[#2F3D8E]">Tareas</p>
                <p className="text-xs text-gray-600">Identificación de acciones</p>
              </div>
            </div>
          </div>
        </div>

        {/* Analyses List */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-[#2F3D8E]">Mis Análisis</h2>
            <Button variant="outline" size="sm" onClick={fetchAnalyses}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
          </div>

          {analyses.length === 0 ? (
            <div className="text-center py-12">
              <FileVideo className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No hay análisis todavía</p>
              <p className="text-sm text-gray-400">Suba un video para comenzar</p>
            </div>
          ) : (
            <div className="space-y-4">
              {analyses.map((analysis) => (
                <div 
                  key={analysis.id}
                  className="border rounded-xl p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#2F3D8E]/10 rounded-lg flex items-center justify-center">
                        <FileVideo className="w-6 h-6 text-[#2F3D8E]" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">{analysis.filename}</h3>
                        <p className="text-sm text-gray-500">{formatDate(analysis.created_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(analysis.status)}
                      {analysis.status === 'completed' && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => viewAnalysis(analysis.id)}
                          >
                            <FileText className="w-4 h-4 mr-1" />
                            Ver
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDownload(analysis.id)}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Word
                          </Button>
                        </>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDelete(analysis.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Recording Preview Modal */}
      {showRecordingModal && recordedVideoUrl && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="recording-preview-modal">
          <div className="bg-white rounded-2xl w-full max-w-3xl overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-[#2F3D8E]">Vista Previa de Grabación</h2>
                  <p className="text-gray-500">Duración: {formatTime(recordingTime)}</p>
                </div>
                <button 
                  onClick={cancelRecording}
                  className="text-gray-400 hover:text-gray-600"
                  data-testid="close-recording-modal-btn"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {/* Video Preview */}
              <div className="bg-black rounded-xl overflow-hidden mb-6">
                <video 
                  src={recordedVideoUrl} 
                  controls 
                  className="w-full max-h-[400px]"
                  data-testid="recording-preview-video"
                />
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="outline"
                  onClick={downloadRecording}
                  className="px-6"
                  data-testid="download-recording-btn"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Descargar Video
                </Button>
                <Button
                  onClick={uploadRecording}
                  className="bg-[#4C8C3B] hover:bg-[#3d7030] text-white px-6"
                  disabled={isUploading}
                  data-testid="upload-recording-btn"
                >
                  {isUploading ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-5 h-5 mr-2" />
                  )}
                  Analizar con IA
                </Button>
                <Button
                  variant="ghost"
                  onClick={cancelRecording}
                  className="text-gray-500"
                  data-testid="cancel-recording-btn"
                >
                  <Trash2 className="w-5 h-5 mr-2" />
                  Descartar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analysis Detail Modal */}
      {showDetailModal && selectedAnalysis && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-[#2F3D8E]">Análisis de Video</h2>
                  <p className="text-gray-500">{selectedAnalysis.filename}</p>
                </div>
                <div className="flex items-center gap-2">
                  {selectedAnalysis.status === 'completed' && (
                    <Button 
                      variant="outline"
                      onClick={() => handleDownload(selectedAnalysis.id)}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Descargar Word
                    </Button>
                  )}
                  <button 
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Executive Summary */}
              <div className="bg-gradient-to-r from-[#2F3D8E] to-[#4C8C3B] rounded-xl p-6 text-white">
                <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                  <FileOutput className="w-5 h-5" />
                  Resumen Ejecutivo
                </h3>
                <p className="whitespace-pre-wrap leading-relaxed">
                  {selectedAnalysis.executive_summary || 'No disponible'}
                </p>
              </div>

              {/* Key Points */}
              <div className="bg-blue-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-[#2F3D8E] mb-3 flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Puntos Clave
                </h3>
                <div className="prose prose-sm max-w-none">
                  <pre className="whitespace-pre-wrap font-sans text-gray-700 bg-white rounded-lg p-4">
                    {selectedAnalysis.key_points || 'No disponible'}
                  </pre>
                </div>
              </div>

              {/* Tasks */}
              <div className="bg-green-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-[#4C8C3B] mb-3 flex items-center gap-2">
                  <ListChecks className="w-5 h-5" />
                  Tareas Identificadas
                </h3>
                <div className="prose prose-sm max-w-none">
                  <pre className="whitespace-pre-wrap font-sans text-gray-700 bg-white rounded-lg p-4">
                    {selectedAnalysis.tasks || 'No disponible'}
                  </pre>
                </div>
              </div>

              {/* Full Transcription */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Transcripción Completa
                </h3>
                <div className="bg-white rounded-lg p-4 max-h-64 overflow-y-auto">
                  <p className="whitespace-pre-wrap text-gray-600 text-sm leading-relaxed">
                    {selectedAnalysis.transcription || 'No disponible'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerPage;
